import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../../Toast';
import { UserAttendance, UserAddRemarkAttendance } from '../../../../services/oprations/User_Bussiness_Logic';
import { ChevronDown, ChevronUp, LogIn, LogOut, MessageSquare, Download, Filter } from 'lucide-react';
import RemarkModal from './RemarkModal';
import useSocket from '../../../../hooks/useSocket';
import * as XLSX from 'xlsx';

const COLUMN_STORAGE_KEY = 'attendance_visible_columns';

const defaultColumns = {
  date: true,
  employee: true,
  workTimeZone: true,
  viewTimeZone: true,
  firstIn: true,
  lastOut: true,
  workTime: true,
  earlyIn: false,
  lateIn: false,
  earlyOut: true,
  lateOut: false,
  undertime: false,
  overtime: false,
  inOutStatus: true,
  status: true,
  remark: true,
  details: true,
};

// ── Export helpers ────────────────────────────────────────────────────────────

const flattenRecord = (record) => {
  const inOutStr = record.INOUT_Times?.length
    ? record.INOUT_Times.map((io) => `${io.type}: ${io.time}`).join(' ; ')
    : '—';

  const statusMap = { P: 'Present', A: 'Absent', L: 'Late', W: 'Weekend', H: 'Holiday' };

  return {
    Date: record.DateString
      ? new Date(record.DateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      : '—',
    Name: record.Name || '—',
    'Employee Code': record.EMP_Code || '—',
    Status: statusMap[record.status] || '—',
    'Work TimeZone': record.WorkTimeZone || '-',
    'View TimeZone': record.ViewTimeZone || '-',
    'First IN Time': record.IN_Time || '—',
    'Last OUT Time': record.OUT_Time || '—',
    'Work Time': record.Work_Time || '—',
    'Early In': record.Erly_In || '—',
    'Late In': record.Late_In || '—',
    'Early Out': record.Erl_Out || '—',
    'Late Out': record.Late_Out || '—',
    'Under Time': record.Under_Time || '-',
    'Over Time': record.Over_Time || '_',
    'IN/OUT Times': inOutStr,
  };
};

const exportToCSV = (rows, filename) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(escape).join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const exportToExcel = (rows, filename) => {
  if (!rows.length) return;
  try {
    const ws = XLSX.utils.json_to_sheet(rows);
    const headers = Object.keys(rows[0]);
    ws['!cols'] = headers.map((h) => ({ wch: Math.max(h.length, ...rows.map((r) => String(r[h] ?? '').length)) + 2 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    XLSX.writeFile(wb, filename);
  } catch (err) {
    console.error('Excel export failed, falling back to CSV:', err);
    exportToCSV(rows, filename.replace('.xlsx', '.csv'));
  }
};

// ── Helper functions for date/time formatting ──────────────────────────────

const parseDateTime = (dateTimeStr) => {
  if (!dateTimeStr || dateTimeStr === '--' || dateTimeStr === 'Invalid Date') {
    return { date: '—', time: '—' };
  }
  
  try {
    // Handle format like "2026-07-01 04:00:03 PM"
    if (typeof dateTimeStr === 'string' && dateTimeStr.includes(' ')) {
      const parts = dateTimeStr.split(' ');
      if (parts.length >= 3) {
        // parts[0] = "2026-07-01", parts[1] = "04:00:03", parts[2] = "PM"
        const datePart = parts[0];
        const timePart = `${parts[1]} ${parts[2]}`;
        
        // Format date as DD/MM/YYYY
        const dateObj = new Date(datePart);
        if (!isNaN(dateObj.getTime())) {
          const day = String(dateObj.getDate()).padStart(2, '0');
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const year = dateObj.getFullYear();
          const formattedDate = `${day}/${month}/${year}`;
          
          // Format time as HH:MM:SS AM/PM
          const timeParts = timePart.split(' ');
          if (timeParts.length === 2) {
            const [hourMinSec, ampm] = timeParts;
            const [hours, minutes, seconds] = hourMinSec.split(':');
            const hour = parseInt(hours);
            // Already in 12-hour format from API
            const hourStr = String(hour).padStart(2, '0');
            return {
              date: formattedDate,
              time: `${hourStr}:${minutes}:${seconds} ${ampm}`
            };
          }
        }
      }
    }
    
    // Fallback: try parsing as date
    const date = new Date(dateTimeStr);
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return {
        date: `${day}/${month}/${year}`,
        time: date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit',
          hour12: true 
        })
      };
    }
    
    return { date: '—', time: '—' };
  } catch {
    return { date: '—', time: '—' };
  }
};

const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr || dateStr === '--' || dateStr === 'Invalid Date') return '—';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '—';
  }
};

const formatTimeHHMMSS = (timeStr) => {
  if (!timeStr || timeStr === '--' || timeStr === 'Invalid Date') return '—';
  try {
    if (typeof timeStr === 'string') {
      // If it's already in format like "04:00:03 PM"
      if (timeStr.includes('AM') || timeStr.includes('PM')) {
        const parts = timeStr.split(' ');
        if (parts.length === 2) {
          const timeParts = parts[0].split(':');
          if (timeParts.length === 3) {
            let hours = parseInt(timeParts[0]);
            const minutes = timeParts[1];
            const seconds = timeParts[2];
            const ampm = parts[1];
            // Keep as is since API already gives 12-hour format
            const hourStr = String(hours).padStart(2, '0');
            return `${hourStr}:${minutes}:${seconds} ${ampm}`;
          }
        }
        return timeStr;
      }
      
      // If it's a time string like "08:13:09"
      if (/^\d{2}:\d{2}:\d{2}$/.test(timeStr)) {
        const [hours, minutes, seconds] = timeStr.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        const hourStr = String(hour12).padStart(2, '0');
        return `${hourStr}:${minutes}:${seconds} ${ampm}`;
      }
      
      const date = new Date(timeStr);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit',
          hour12: true 
        });
      }
    }
    return '—';
  } catch {
    return '—';
  }
};

// ── Main Component ────────────────────────────────────────────────────────────

const Attendance = () => {
  const dispatch = useDispatch();
  const socket = useSocket();
  const toast = useToast();

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 2);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const [dateRange, setDateRange] = useState({
    from: firstDayOfMonth.toISOString().split('T')[0],
    to: lastDayOfMonth.toISOString().split('T')[0],
  });

  const [expandedRows, setExpandedRows] = useState({});
  const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [remarkLoading, setRemarkLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showColumnMenu, setShowColumnMenu] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(COLUMN_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const sanitised = Object.fromEntries(
          Object.entries(defaultColumns).map(([k]) => [
            k,
            typeof parsed[k] === 'boolean' ? parsed[k] : defaultColumns[k],
          ])
        );
        return sanitised;
      }
    } catch {}
    return { ...defaultColumns };
  });

  const toggleColumn = (column) => {
    setVisibleColumns((prev) => {
      const updated = { ...prev, [column]: !prev[column] };
      try {
        localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(updated));
      } catch {}
      return updated;
    });
  };

  const { userDetails } = useSelector((s) => s.employeeAuth?.data || s.employeeAuth || {});
  const { userAttendanceData } = useSelector((s) => s.userAttendance?.data || s.userAttendance || []);
  const userData = userDetails;
  const token = localStorage.getItem('token') || userData?.token;

  const attendanceData = [...(userAttendanceData || [])].sort(
    (a, b) => new Date(b.DateString) - new Date(a.DateString)
  );

   const fetchAttendance =useCallback( async () => {
    await dispatch(UserAttendance(dateRange.from, dateRange.to, token));
  },[dateRange.from, dateRange.to, dispatch, token]);

  useEffect(() => {
    if (token && dateRange.from && dateRange.to) fetchAttendance();
  }, [dateRange.from, dateRange.to, token, fetchAttendance]);


  const toDateStr = (val) => {
    if (!val) return '';
    if (val instanceof Date) return val.toISOString().slice(0, 10);
    const s = String(val);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    if (s.length >= 10) return s.slice(0, 10);
    return s;
  };

  useEffect(() => {
    if (!socket) return;
    const handleAttendanceUpdate = () => fetchAttendance();
    socket.on('Attendance_Status', handleAttendanceUpdate);
    return () => socket.off('Attendance_Status', handleAttendanceUpdate);
  }, [socket, dateRange.from, dateRange.to, fetchAttendance]);

  const handleDateChange = (type, value) => setDateRange((prev) => ({ ...prev, [type]: value }));
  const toggleRow = (id) => setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  const handleAddRemark = (record) => { setSelectedRecord(record); setIsRemarkModalOpen(true); };

  const handleSubmitRemark = async (remark) => {
    if (!selectedRecord || !token) return;
    setRemarkLoading(true);
    const res = await dispatch(UserAddRemarkAttendance(selectedRecord._id, { Note: remark }, token));
    if (res?.data?.success) {
      setRemarkLoading(false);
      setIsRemarkModalOpen(false);
      setSelectedRecord(null);
      await fetchAttendance();
      toast.success(res.data.message || 'Remark added successfully');
    } else {
      toast.error(res.data.message || 'Failed to add remark');
    }
  };

  const handleExport = async (format = 'xlsx') => {
    if (!attendanceData?.length) { toast.error('No attendance data to export.'); return; }
    setExportLoading(true);
    try {
      const rows = attendanceData.map(flattenRecord);
      const filename = `Attendance_${dateRange.from}_to_${dateRange.to}.${format}`;
      if (format === 'csv') exportToCSV(rows, filename);
      else await exportToExcel(rows, filename);
      toast.success(`Exported as ${filename}`);
    } catch (err) {
      console.error(err);
      toast.error('Export failed. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const formatDate = (ds) =>
    ds ? new Date(ds).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
  const fmt = (t) => t || '—';

  const getDatesInRange = (from, to) => {
    const dates = [];
    const end = new Date(to);
    for (let d = new Date(from); d <= end; d.setDate(d.getDate() + 1)) dates.push(new Date(d).toISOString().slice(0, 10));
    return dates;
  };

  const processStats = () => {
    if (!attendanceData || attendanceData.length === 0)
      return { presentDays: 0, absentDays: 0, attendancePct: 0, totalLateDays: 0, totalOvertime: 0 };

    const todayStr = new Date().toISOString().slice(0, 10);
    const effectiveTo = dateRange.to < todayStr ? dateRange.to : todayStr;
    const elapsedDays = getDatesInRange(dateRange.from, effectiveTo).length;

    let presentDays = 0, lateDays = 0, leaveDays = 0, holidayDays = 0, totalOvertimeHours = 0;

    attendanceData.forEach((record) => {
      const dateStr = toDateStr(record.DateString);
      if (dateStr < dateRange.from || dateStr > dateRange.to) return;
      if (record.status === 'P') {
        presentDays++;
        if (record.Late_In && record.Late_In !== '00:00' && record.Late_In !== '00:00:00') lateDays++;
      }
      if (record.status === 'L') leaveDays++;
      if (record.status === 'H') holidayDays++;
      if (record.Over_Time && record.Over_Time !== '00:00' && record.Over_Time !== '00:00:00') {
        const parts = record.Over_Time.split(':');
        totalOvertimeHours += (parseInt(parts[0]) || 0) + (parseInt(parts[1]) || 0) / 60;
      }
    });

    const absentDays = Math.max(0, elapsedDays - presentDays - leaveDays - holidayDays);
    const attendancePct = elapsedDays > 0 ? ((presentDays / elapsedDays) * 100).toFixed(1) : 0;
    return { presentDays, absentDays, attendancePct, totalLateDays: lateDays, totalOvertime: totalOvertimeHours };
  };

  const stats = processStats();
  const statCards = [
    { label: 'Present Days', value: stats.presentDays },
    { label: 'Absent Days', value: stats.absentDays },
    { label: 'Attendance Rate', value: `${stats.attendancePct}%` },
    { label: 'Late Arrivals', value: stats.totalLateDays },
    { label: 'Overtime', value: `${stats.totalOvertime.toFixed(1)} H` },
  ];

  const hiddenCount = Object.keys(defaultColumns).filter((k) => !visibleColumns[k]).length;

  return (
    <div className="p-4 transition-colors duration-300 bg-gray-50 dark:bg-black md:p-6 lg:p-8">

      {/* ── Header ── */}
      <div className="mb-5 md:mb-7">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white md:text-2xl">Attendance Records</h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="relative">
              <button
                onClick={() => setShowColumnMenu((prev) => !prev)}
                className="inline-flex items-center gap-1.5 px-2 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 bg-white border border-gray-300 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 md:px-4 md:gap-2"
              >
                <Filter size={14} />
                Columns
                {hiddenCount > 0 && (
                  <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-blue-500 rounded-full leading-none">
                    {hiddenCount}
                  </span>
                )}
                <ChevronDown size={13} className={`transition-transform duration-200 ${showColumnMenu ? 'rotate-180' : ''}`} />
              </button>

              {showColumnMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowColumnMenu(false)} />
                  <div className="absolute right-0 z-20 w-56 p-3 mt-2 overflow-hidden text-gray-400 bg-gray-100 border border-gray-400 shadow-xl shadow-gray-500 dark:bg-gray-900 dark:border-gray-700 rounded-xl">
                    <div className="space-y-2">
                      {Object.entries({
                        date: 'Date',
                        employee: 'Employee',
                        workTimeZone: 'Work Offset',
                        viewTimeZone: 'View Timezone',
                        firstIn: 'First IN',
                        lastOut: 'Last OUT',
                        workTime: 'Work Time',
                        earlyIn: 'Early In',
                        lateIn: 'Late In',
                        earlyOut: 'Early Out',
                        lateOut: 'Late Out',
                        undertime: 'Under Time',
                        overtime: 'Over Time',
                        inOutStatus: 'In/Out',
                        status: 'Status',
                        remark: 'Remark',
                        details: 'Details',
                      }).map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={visibleColumns[key]}
                            onChange={() => toggleColumn(key)}
                            className="border-gray-300 rounded dark:border-gray-600"
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <ExportButton onExport={handleExport} loading={exportLoading} />
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
          <div className="flex items-center flex-1 gap-2">
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => handleDateChange('from', e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-700 transition-all duration-200 bg-white border border-gray-300 rounded-lg dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
              max={dateRange.to}
            />
            <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">to</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => handleDateChange('to', e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-700 transition-all duration-200 bg-white border border-gray-300 rounded-lg dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
              min={dateRange.from}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
      </div>

      {/* <div style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}> */}
      <div>
        {/* ── Stats cards ── */}
        <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-3 md:grid-cols-5 md:mb-6">
          {statCards.map((card, idx) => (
            <div
              key={idx}
              className={`p-3 transition-all duration-300 bg-white border border-gray-300 dark:bg-gray-900 rounded-xl hover:shadow-md md:p-4 ${
                idx === 4 ? 'col-span-2 sm:col-span-1' : ''
              }`}
            >
              <p className="text-xs font-medium leading-tight tracking-wide text-gray-500 uppercase">{card.label}</p>
              <p className="mt-1.5 text-xl font-bold text-gray-900 dark:text-white md:text-2xl">{card.value}</p>
            </div>
          ))}
        </div>

        {/* ── Mobile card list ── */}
        <div className="space-y-2 md:hidden">
          <div className="flex items-center justify-between px-1 mb-1">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Daily records</span>
            <span className="text-xs text-gray-500">{attendanceData?.length || 0} records</span>
          </div>

          {attendanceData?.length > 0 ? (
            attendanceData.map((record, idx) => (
              <MobileRecordCard
                key={record._id || idx}
                record={record}
                expanded={!!expandedRows[record._id]}
                onToggle={() => toggleRow(record._id)}
                onRemark={() => handleAddRemark(record)}
                formatDate={formatDate}
                fmt={fmt}
              />
            ))
          ) : (
            <div className="py-10 text-sm text-center text-gray-500">
              No records found for {dateRange.from} to {dateRange.to}
            </div>
          )}
        </div>

        {/* ── Desktop table ── */}
        <div className="hidden overflow-hidden transition-all duration-300 bg-white border border-gray-300 md:block dark:bg-gray-900 rounded-xl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Daily records</span>
            <span className="text-xs text-gray-500">{attendanceData?.length || 0} records</span>
          </div>

          <div className="overflow-x-auto overflow-y-auto">
            <div className="min-w-[900px]">
              {/* Table Header - Compact */}
              <div className="border-b border-gray-200 bg-gray-50 dark:bg-gray-800/50 dark:border-gray-600">
                <div className="flex items-center gap-2 px-3 py-2">
                  {visibleColumns.date && <div className="w-[85px] text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase flex-shrink-0">Date</div>}
                  {visibleColumns.employee && <div className="w-[110px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Employee</div>}
                  {visibleColumns.workTimeZone && <div className="w-[75px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Work Offset</div>}
                  {visibleColumns.viewTimeZone && <div className="w-[85px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">View Timezone</div>}
                  {visibleColumns.firstIn && <div className="w-[80px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">First IN</div>}
                  {visibleColumns.lastOut && <div className="w-[80px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Last OUT</div>}
                  {visibleColumns.workTime && <div className="w-[80px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Work</div>}
                  {visibleColumns.earlyIn && <div className="w-[80px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Early In</div>}
                  {visibleColumns.lateIn && <div className="w-[80px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Late In</div>}
                  {visibleColumns.earlyOut && <div className="w-[80px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Early Out</div>}
                  {visibleColumns.lateOut && <div className="w-[80px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Late Out</div>}
                  {visibleColumns.undertime && <div className="w-[80px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Under Time</div>}
                  {visibleColumns.overtime && <div className="w-[80px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Over Time</div>}
                  {visibleColumns.inOutStatus && <div className="w-[55px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">In/Out</div>}
                  {visibleColumns.status && <div className="w-[120px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Status</div>}
                  {visibleColumns.remark && <div className="w-[70px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Remark</div>}
                  {visibleColumns.details && <div className="w-[40px] text-[11px] font-medium text-gray-500 uppercase dark:text-gray-400 flex-shrink-0">Details</div>}
                </div>
              </div>

              {/* Table Body - Compact */}
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {attendanceData?.length > 0 ? (
                  attendanceData.map((record, idx) => {
                    const inDateTime = parseDateTime(record.IN_Time);
                    const outDateTime = parseDateTime(record.OUT_Time);
                    
                    return (
                    <React.Fragment key={record._id || idx}>
                      <div className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        {visibleColumns.date && (
                          <div className="w-[85px] flex-shrink-0">
                            {/* <span className="text-xs font-medium text-gray-900 dark:text-white">{formatDateDDMMYYYY(record.DateString)}</span> */}
                            <div className="leading-tight">
                              <div className="text-[9.5px] pb-0.5">{parseDateTime(record.DateString).date}</div>
                              <div className="text-[9px] font-medium text-gray-500 dark:text-gray-700">
                               {parseDateTime(record.DateString).time}
                              </div>
                            </div>
                          </div>
                        )}
                        {visibleColumns.employee && (
                          <div className="w-[110px] flex-shrink-0">
                            <p className="text-xs font-medium text-gray-900 truncate dark:text-white">{record.Name}</p>
                            <p className="text-[10px] text-gray-500">{record.EMP_Code}</p>
                          </div>
                        )}
                        {visibleColumns.workTimeZone && (
                          <div className="w-[75px] flex-shrink-0 text-xs text-gray-700 dark:text-gray-300 truncate">{record.WorkTimeZone || '—'}</div>
                        )}
                        {visibleColumns.viewTimeZone && (
                          <div className="w-[85px] flex-shrink-0 text-xs text-gray-700 dark:text-gray-300 truncate">{record.ViewTimeZone || '—'}</div>
                        )}
                        {visibleColumns.firstIn && (
                          <div className="w-[80px] flex-shrink-0">
                            {record.IN_Time && record.IN_Time !== '--:--:--' && record.IN_Time !== 'Invalid Date' ? (
                              <div className="leading-tight">
                                <div className="text-[9.5px] pb-0.5">{inDateTime.date}</div>
                                <div className="text-[9px] font-medium text-gray-500 dark:text-gray-700">
                                  {inDateTime.time}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-700">00:00:00</span>
                            )}
                          </div>
                        )}
                        {visibleColumns.lastOut && (
                          <div className="w-[80px] flex-shrink-0">
                            {record.OUT_Time && record.OUT_Time !== '--:--:--' && record.OUT_Time !== 'Invalid Date' ? (
                              <div className="leading-tight">
                                <div className="text-[9.5px] pb-0.5">{outDateTime.date}</div>
                                <div className="text-[9px] font-medium text-gray-500 dark:text-gray-700">
                                  {outDateTime.time}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-700">00:00:00</span>
                            )}
                          </div>
                        )}
                        {visibleColumns.workTime && (
                          <div className={`w-[80px] flex-shrink-0 text-xs ${record.Work_Time !== '00:00:00' ? "text-gray-700 dark:text-gray-300" : "text-gray-500 dark:text-gray-700"}`}>{record.Work_Time || '—'}</div>
                        )}
                        {visibleColumns.earlyIn && (
                        <div className={`w-[80px] flex-shrink-0 text-xs ${record.Erly_In !== '00:00:00' ? "text-gray-700 dark:text-gray-300" : "text-gray-500 dark:text-gray-700"} `}>
                            {record.Erly_In && record.Erly_In !== '00:00:00' ? formatTimeHHMMSS(record.Erly_In) : '00:00:00'}
                          </div>
                        )}
                        {visibleColumns.lateIn && (
                        <div className={`w-[80px] flex-shrink-0 text-xs ${record.Late_In !== '00:00:00' ? "text-gray-700 dark:text-gray-300" : "text-gray-500 dark:text-gray-700"} `}>
                            {record.Late_In && record.Late_In !== '00:00:00' ? formatTimeHHMMSS(record.Late_In) : '00:00:00'}
                          </div>
                        )}
                        {visibleColumns.earlyOut && (
                        <div className={`w-[80px] flex-shrink-0 text-xs ${record.Erl_Out !== '00:00:00' ? "text-gray-700 dark:text-gray-300" : "text-gray-500 dark:text-gray-700"} `}>
                            {record.Erl_Out && record.Erl_Out !== '00:00:00' ? formatTimeHHMMSS(record.Erl_Out) : '00:00:00'}
                          </div>
                        )}
                        {visibleColumns.lateOut && (
                        <div className={`w-[80px] flex-shrink-0 text-xs ${record.Late_Out !== '00:00:00' ? "text-gray-700 dark:text-gray-300" : "text-gray-500 dark:text-gray-700"} `}>
                            {record.Late_Out && record.Late_Out !== '00:00:00' ? formatTimeHHMMSS(record.Late_Out) : '00:00:00'}
                          </div>
                        )}
                        {visibleColumns.undertime && (
                        <div className={`w-[80px] flex-shrink-0 text-xs ${record.Under_Time !== '00:00:00' ? "text-gray-700 dark:text-gray-300" : "text-gray-500 dark:text-gray-700"} `}>
                            {record.Under_Time && record.Under_Time !== '00:00:00' ? formatTimeHHMMSS(record.Under_Time) : '00:00:00'}
                          </div>
                        )}
                        {visibleColumns.overtime && (
                        <div className={`w-[80px] flex-shrink-0 text-xs ${record.Over_Time !== '00:00:00' ? "text-gray-700 dark:text-gray-300" : "text-gray-500 dark:text-gray-700"} `}>
                            {record.Over_Time && record.Over_Time !== '00:00:00' ? formatTimeHHMMSS(record.Over_Time) : '00:00:00'}
                          </div>
                        )}
                        {visibleColumns.inOutStatus && (
                          <div className="w-[55px] flex-shrink-0"><InOutBadge status={record.InOut_Status} /></div>
                        )}
                        {visibleColumns.status && (
                          <div className="w-[120px] flex-shrink-0"><StatusBadge status={record.status} record={record} formatTimeHHMMSS={formatTimeHHMMSS} /></div>
                        )}
                        {visibleColumns.remark && (
                          <div className="w-[70px] flex-shrink-0">
                            <button
                              onClick={() => handleAddRemark(record)}
                              className="inline-flex items-center gap-1 px-1.5 py-1 text-[10px] font-medium text-blue-600 dark:text-blue-100 bg-blue-100/20 border border-blue-400 rounded hover:bg-blue-100/40 whitespace-nowrap"
                            >
                              <MessageSquare size={10} /> Add
                            </button>
                          </div>
                        )}
                        {visibleColumns.details && (
                          <div className="w-[40px] flex-shrink-0">
                            {(record.INOUT_Times?.length > 0 || record.Note) && (
                              <button onClick={() => toggleRow(record._id)} className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                {expandedRows[record._id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {expandedRows[record._id] && (
                        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/30">
                          <ExpandedDetails record={record} fmt={fmt} formatTimeHHMMSS={formatTimeHHMMSS} />
                        </div>
                      )}
                    </React.Fragment>
                    );
                  })
                ) : (
                  <div className="px-4 py-12 text-center">
                    <p className="text-sm text-gray-500">No records found for {dateRange.from} to {dateRange.to}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <RemarkModal
        isOpen={isRemarkModalOpen}
        onClose={() => { setIsRemarkModalOpen(false); setSelectedRecord(null); }}
        onSubmit={handleSubmitRemark}
        record={selectedRecord}
        loading={remarkLoading}
      />
    </div>
  );
};

// ── Mobile Record Card ────────────────────────────────────────────────────────

const MobileRecordCard = ({ record, expanded, onToggle, onRemark, formatDate, fmt }) => {
  const hasDetails = record.INOUT_Times?.length > 0 || record.Note || record.Remark;
  const inDateTime = parseDateTime(record.IN_Time);
  const outDateTime = parseDateTime(record.OUT_Time);

  return (
    <div className="overflow-hidden bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-800 rounded-xl">
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate dark:text-white">{record.Name}</p>
            <p className="text-xs text-gray-500 font-extralight">{record.EMP_Code} • {formatDateDDMMYYYY(record.DateString)}</p>
          </div>
          <div>
            <div className="flex items-center gap-2 shrink-0">
              <StatusBadge status={record.status} record={record} formatTimeHHMMSS={formatTimeHHMMSS} />
              <InOutBadge status={record.InOut_Status} />
            </div>
            <p className="mt-1 text-xs text-gray-500 text-end font-extralight">{record.ViewTimeZone}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 border-t border-gray-100 divide-x divide-gray-100 dark:divide-gray-800 dark:border-gray-800">
        <div className="px-2 py-2 text-center">
          <p className="text-[10px] text-gray-400 mb-0.5">First IN</p>
          {record.IN_Time && record.IN_Time !== '--' && record.IN_Time !== 'Invalid Date' ? (
            <>
              <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{inDateTime.time}</p>
              <p className="text-[10px] text-gray-500">{inDateTime.date}</p>
            </>
          ) : (
            <p className="text-xs font-medium text-gray-800 dark:text-gray-200">—</p>
          )}
        </div>
        <div className="px-2 py-2 text-center">
          <p className="text-[10px] text-gray-400 mb-0.5">Last OUT</p>
          {record.OUT_Time && record.OUT_Time !== '--' && record.OUT_Time !== 'Invalid Date' ? (
            <>
              <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{outDateTime.time}</p>
              <p className="text-[10px] text-gray-500">{outDateTime.date}</p>
            </>
          ) : (
            <p className="text-xs font-medium text-gray-800 dark:text-gray-200">—</p>
          )}
        </div>
        <div className="px-2 py-2 text-center">
          <p className="text-[10px] text-gray-400 mb-0.5">Work Time</p>
          <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{record.Work_Time || '—'}</p>
        </div>
      </div>

      <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40">
        <button
          onClick={onRemark}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100"
        >
          <MessageSquare size={11} /> Add Remark
        </button>

        {hasDetails && (
          <button
            onClick={onToggle}
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            {expanded ? 'Less' : 'Details'}
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        )}
      </div>

      {expanded && hasDetails && (
        <div className="px-4 py-3 space-y-3 border-t border-gray-100 dark:border-gray-800">
          <ExpandedDetails record={record} fmt={fmt} formatTimeHHMMSS={formatTimeHHMMSS} />
        </div>
      )}
    </div>
  );
};

// ── Shared Expanded Details ───────────────────────────────────────────────────

const ExpandedDetails = ({ record, fmt, formatTimeHHMMSS }) => (
  <>
    {record.INOUT_Times?.length > 0 && (
      <div>
        <p className="mb-2 text-xs font-medium tracking-wide text-gray-400 uppercase">Check in/out history</p>
        <div className="flex flex-wrap gap-2">
          {record.INOUT_Times.map((io, i) => {
            const isIn = io.type === 'IN';
            return (
              <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isIn ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                {isIn ? <LogIn size={12} className="text-green-600" /> : <LogOut size={12} className="text-red-600" />}
                <span className={`text-xs font-medium ${isIn ? 'text-green-600' : 'text-red-600'}`}>{isIn ? 'In' : 'Out'}</span>
                <span className="text-xs text-gray-700">{fmt(io.time)}</span>
              </div>
            );
          })}
        </div>
      </div>
    )}

    <div className="flex flex-wrap gap-2 mt-3">
      {record.Late_In && record.Late_In !== '00:00' && record.Late_In !== '00:00:00' && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-yellow-100/20 border border-yellow-200/20 text-xs font-medium text-yellow-500 dark:text-yellow-200">
          Late arrival · {formatTimeHHMMSS(record.Late_In)}
        </div>
      )}
      {record.Erl_Out && record.Erl_Out !== '00:00' && record.Erl_Out !== '00:00:00' && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-50 border border-orange-200 text-xs font-medium text-orange-700">
          Early exit · {formatTimeHHMMSS(record.Erl_Out)}
        </div>
      )}
    </div>

    {record.Remark && (
      <div className='mt-3'>
        <p className="mb-1.5 text-xs font-medium tracking-wide text-gray-400 uppercase">Note / Remark</p>
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-blue-100/20 border border-blue-400 max-w-xl">
          <MessageSquare size={13} className="dark:text-gray-400 text-blue-500 mt-0.5 shrink-0" />
          <p className="text-xs leading-relaxed text-blue-800 dark:text-gray-400">{record.Remark}</p>
        </div>
      </div>
    )}
  </>
);

// ── Export Button ─────────────────────────────────────────────────────────────

const ExportButton = ({ onExport, loading }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen((prev) => !prev)}
        disabled={loading}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white transition-colors duration-200 bg-gradient-to-br from-blue-500 to-blue-700 border border-blue-500 rounded-lg shadow-sm hover:bg-orange-600 disabled:opacity-60 md:px-4 md:gap-2"
      >
        <Download size={14} />
        <span className="hidden sm:inline">{loading ? 'Exporting…' : 'Export'}</span>
        <ChevronDown size={13} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-2 overflow-hidden bg-white border border-gray-200 shadow-lg w-44 dark:bg-gray-900 dark:border-gray-700 rounded-xl">
            <button
              onClick={() => { setOpen(false); onExport('xlsx'); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-gray-800 transition-colors"
            >
              <span className="flex items-center justify-center w-5 h-5 text-xs font-bold text-green-700 bg-green-100 rounded">X</span>
              Export as Excel
            </button>
            <div className="border-t border-gray-100 dark:border-gray-800" />
            <button
              onClick={() => { setOpen(false); onExport('csv'); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-gray-800 transition-colors"
            >
              <span className="flex items-center justify-center w-5 h-5 text-xs font-bold text-blue-400 rounded bg-blue-100/20 dark:bg-blue-100 dark:text-blue-700">C</span>
              Export as CSV
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ── StatusBadge ───────────────────────────────────────────────────────────────
const StatusBadge = ({ status, record, formatTimeHHMMSS }) => {
  if (status === 'P') {
    const late = record?.Late_In;
    const earlyExit = record?.Erl_Out;
    
    const lastPunchType = record?.INOUT_Times?.[record.INOUT_Times.length - 1]?.type?.toUpperCase();
    
    if (lastPunchType === "IN") {
      if (late && late !== '00:00' && late !== '00:00:00') {
          return <span className="inline-flex px-1.5 py-0.5 text-[10px] rounded-full bg-yellow-100/20 dark:text-yellow-500 text-yellow-700 whitespace-nowrap">Late · {formatTimeHHMMSS(late)}</span>;
      }
      return <span className="inline-flex px-1.5 py-0.5 text-[10px] text-green-700 bg-green-100 rounded-full">Present</span>;
    }

    if (lastPunchType === "OUT") {
      if (earlyExit && earlyExit !== '00:00' && earlyExit !== '00:00:00') {
        return <span className="inline-flex px-1.5 py-0.5 text-[10px] rounded-full bg-orange-100 text-orange-700 whitespace-nowrap">Early exit · {formatTimeHHMMSS(earlyExit)}</span>;
      }

      if (late && late !== '00:00' && late !== '00:00:00') {
         return <span className="inline-flex px-1.5 py-0.5 text-[10px] rounded-full bg-yellow-100/20 dark:text-yellow-500 text-yellow-700 whitespace-nowrap">Late · {formatTimeHHMMSS(late)}</span>;
      }

      return <span className="inline-flex px-1.5 py-0.5 text-[10px] text-green-700 bg-green-100 rounded-full">Present</span>;
    }

    return <span className="inline-flex px-1.5 py-0.5 text-[10px] text-green-700 bg-green-100 rounded-full">Present</span>;
  }
  
  if (status === 'A') return <span className="inline-flex px-1.5 py-0.5 text-[10px] text-red-700 bg-red-100 rounded-full">Absent</span>;
  if (status === 'H') return <span className="inline-flex px-1.5 py-0.5 text-[10px] rounded-full bg-cyan-100 text-cyan-700">Holiday</span>;
  if (status === 'L') return <span className="inline-flex px-1.5 py-0.5 text-[10px] text-purple-700 bg-purple-100 rounded-full">Leave</span>;
  return <span className="inline-flex px-1.5 py-0.5 text-[10px] text-gray-600 bg-gray-100 rounded-full">{status || '—'}</span>;
};

// ── InOutBadge ────────────────────────────────────────────────────────────────

const InOutBadge = ({ status }) => {
  if (status === 'IN')
    return (
      <div className="flex items-center gap-1">
        <div className="w-1 h-1 bg-green-500 rounded-full shrink-0" />
        <span className="text-[10px] font-medium text-green-600 whitespace-nowrap">In</span>
      </div>
    );
  if (status === 'OUT')
    return (
      <div className="flex items-center gap-1">
        <div className="w-1 h-1 bg-gray-400 rounded-full shrink-0" />
        <span className="text-[10px] font-medium text-gray-500 whitespace-nowrap">Out</span>
      </div>
    );
  return null;
};

export default Attendance;
